import { authenticatedApi } from "../utils/api";
import type { ApiResponse } from "../types";

export interface DataCoverageMetrics {
  participantId: string;
  participantName: string;
  deviceType: "fitbit" | "healthkit" | "both";
  dataPoints: number;
  coverage: number; // percentage
  lastSyncDate?: string;
  dateRange?: {
    from: string;
    to: string;
  };
}

export interface StudyDataCoverageMetrics {
  studyId: string;
  studyName: string;
  totalParticipants: number;
  participantsWithData: number;
  averageCoverage: number;
  lastUpdated: string;
}

export interface ParticipantCoverageSummaryRequest {
  fromDate: string;
  endDate: string;
  preset?: string | null;
  refreshIfMissing?: boolean;
}

export interface MissingDataAnalysisRequest {
  fromDate: string;
  endDate: string;
  preset?: string | null;
  refreshIfMissing?: boolean;
}

export interface MissingDataDateRange {
  startDate: string;
  endDate: string;
  dayCount: number;
}

export interface FitbitRefreshRangeStatus extends MissingDataDateRange {
  statusMessage: string;
  refreshed?: boolean;
}

export interface ParticipantFetchMetadata {
  source: string;
  statusMessage: string;
  refreshedFromApi: boolean;
  missingFitbitDateRanges?: MissingDataDateRange[];
  attemptedFitbitRefreshRanges?: FitbitRefreshRangeStatus[];
  failedFitbitRefreshRanges?: FitbitRefreshRangeStatus[];
}

export interface StudyFailedFitbitRefresh {
  participantId: string;
  participantName: string;
  ranges: FitbitRefreshRangeStatus[];
}

export interface StudyFetchMetadata {
  source: string;
  statusMessage: string;
  refreshedFromApi: boolean;
  participantsWithMissingFitbitData: number;
  participantsRefreshedFromApi: number;
  participantsWithFailedFitbitRefresh: number;
  failedFitbitRefreshes: StudyFailedFitbitRefresh[];
}

export interface MissingDataAnalysisResponse {
  participant: {
    participantId: string;
    participantName: string;
    deviceType: "fitbit" | "healthkit" | "both" | "none";
  };
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  keyMetrics: string[];
  summary: {
    datesWithAnyRows: number;
    datesPresentNoRealData: number;
    datesWithNoDataMarker: number;
    placeholderRangeCount: number;
    daysWithCoverage: number;
    daysWithoutCoverage: number;
    coveragePct: number;
    noCoverageRangeCount: number;
  };
  datesPresentNoRealData: string[];
  datesWithoutCoverage: string[];
  noCoverageDateRanges: MissingDataDateRange[];
  placeholderDateRanges: MissingDataDateRange[];
  fetch?: ParticipantFetchMetadata;
}

export interface MetricStatsRequest {
  metricKey: string;
  fromDate: string;
  endDate: string;
  aggregationType?: "none" | "weekly" | "monthly" | "yearly";
  refreshIfMissing?: boolean;
}

export interface MetricStatsAggregationPeriod {
  label: string;
  startDate: string;
  endDate: string;
  daysWithData: number;
  daysWithoutData: number;
  coveragePct: number;
  missingDates: string[];
  minValue: number | null;
  maxValue: number | null;
  meanValue: number | null;
}

export interface MetricStatsResponse {
  metric: {
    key: string;
    label: string;
  };
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
  };
  statistics: {
    minValue: number | null;
    maxValue: number | null;
    meanValue: number | null;
    daysWithData: number;
    daysWithoutData: number;
    coveragePct: number;
    missingDates: string[];
  };
  aggregation: {
    type: "none" | "weekly" | "monthly" | "yearly";
    periods: MetricStatsAggregationPeriod[];
  };
  fetch?: ParticipantFetchMetadata;
}

export interface ParticipantCoverageMetricRow {
  metricKey: string;
  metricLabel: string;
  firstDate: string | null;
  lastDate: string | null;
  daysWithData: number;
  coveragePct: number;
  missingDataCount: number;
  missingDates: string[];
}

export interface ParticipantCoverageSummaryResponse {
  participant: {
    participantId: string;
    participantName: string;
    participantEmail: string | null;
    deviceType: "fitbit" | "healthkit" | "both" | "none";
  };
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  summary: {
    metricsWithData: number;
    totalMetrics: number;
    avgCoveragePct: number;
  };
  metrics: ParticipantCoverageMetricRow[];
  fetch: ParticipantFetchMetadata;
}

export interface StudyMetricCoverageRequest {
  fromDate: string;
  endDate: string;
  preset?: string | null;
  deviceType: "fitbit" | "healthkit" | "both";
  refreshIfMissing?: boolean;
}

export interface AggregatedMetricCoverage {
  metricKey: string;
  metricLabel: string;
  participantCount: number;
  avgCoveragePct: number;
  minCoveragePct: number;
  maxCoveragePct: number;
  participantsWithData: number;
}

export interface StudyMetricCoverageResponse {
  study: {
    studyId: string;
    studyName: string;
    totalParticipants: number;
  };
  selectedDeviceType: "fitbit" | "healthkit" | "both";
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  summary: {
    participantsAnalyzed: number;
    avgCoveragePct: number;
  };
  metrics: AggregatedMetricCoverage[];
  fetch?: StudyFetchMetadata;
}

export interface StudyMetricDetailRequest {
  fromDate: string;
  endDate: string;
  preset?: string | null;
  deviceType: "fitbit" | "healthkit" | "both";
  metricKey: string;
  refreshIfMissing?: boolean;
}

export interface StudyMetricCoverageHeatmapRequest {
  fromDate: string;
  endDate: string;
  aggregationType: "daily" | "weekly" | "monthly" | "yearly";
  selectedMetrics: string[];
  deviceType: "fitbit" | "healthkit" | "both";
  refreshIfMissing?: boolean;
}

export interface HeatmapData {
  metricLabels: string[];
  periodLabels: string[];
  coverageMatrix: number[][];
}

export interface StudyMetricCoverageHeatmapResponse {
  study: {
    studyId: string;
    studyName: string;
    totalParticipants: number;
  };
  coverage: {
    fromDate: string;
    endDate: string;
    aggregationType: "daily" | "weekly" | "monthly" | "yearly";
    deviceType: "fitbit" | "healthkit" | "both";
  };
  fetch?: StudyFetchMetadata;
  heatmap: HeatmapData;
}

export interface ParticipantMetricDetail {
  participantId: string;
  participantName: string;
  coveragePct: number;
  daysWithData: number;
  values: number[];
  minValue: number | null;
  maxValue: number | null;
  meanValue: number | null;
}

export interface MetricCoverageDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface MetricDetailAnalysis {
  metricKey: string;
  metricLabel: string;
  participantCount: number;
  participantsWithData: number;
  statistics: {
    minValue: number | null;
    maxValue: number | null;
    meanValue: number | null;
    avgCoveragePct: number;
  };
  participants: ParticipantMetricDetail[];
  coverageDistribution: MetricCoverageDistribution[];
  lowestCoverageParticipants: ParticipantMetricDetail[];
}

export interface StudyMetricDetailResponse {
  study: {
    studyId: string;
    studyName: string;
    totalParticipants: number;
  };
  selectedDeviceType: "fitbit" | "healthkit" | "both";
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  analysis: MetricDetailAnalysis | null;
  participantsAnalyzed: number;
  fetch?: StudyFetchMetadata;
}

export class DataCoverageService {
  /**
   * Get data coverage metrics for a specific participant
   */
  static async getParticipantCoverage(
    participantId: string,
    token: string,
  ): Promise<ApiResponse<DataCoverageMetrics>> {
    return authenticatedApi.get<ApiResponse<DataCoverageMetrics>>(
      `/api/data-coverage/participant/${participantId}`,
      token,
    );
  }

  /**
   * Get data coverage metrics for an entire study
   */
  static async getStudyCoverage(
    studyId: string,
    token: string,
  ): Promise<ApiResponse<StudyDataCoverageMetrics>> {
    return authenticatedApi.get<ApiResponse<StudyDataCoverageMetrics>>(
      `/api/data-coverage/study/${studyId}`,
      token,
    );
  }

  /**
   * Get detailed coverage report for a participant
   */
  static async getParticipantCoverageReport(
    participantId: string,
    token: string,
  ): Promise<ApiResponse<{ [key: string]: any }>> {
    return authenticatedApi.get<ApiResponse<{ [key: string]: any }>>(
      `/api/data-coverage/participant/${participantId}/report`,
      token,
    );
  }

  static async getParticipantSummaryStats(
    participantId: string,
    payload: ParticipantCoverageSummaryRequest,
    token: string,
  ): Promise<ApiResponse<ParticipantCoverageSummaryResponse>> {
    return authenticatedApi.post<
      ApiResponse<ParticipantCoverageSummaryResponse>
    >(
      `/api/data-coverage/participant/${participantId}/summary-stats`,
      payload,
      token,
    );
  }

  static async getMetricStats(
    participantId: string,
    payload: MetricStatsRequest,
    token: string,
  ): Promise<ApiResponse<MetricStatsResponse>> {
    return authenticatedApi.post<ApiResponse<MetricStatsResponse>>(
      `/api/data-coverage/participant/${participantId}/metric-stats`,
      payload,
      token,
    );
  }

  static async getMissingDataAnalysis(
    participantId: string,
    payload: MissingDataAnalysisRequest,
    token: string,
  ): Promise<ApiResponse<MissingDataAnalysisResponse>> {
    return authenticatedApi.post<ApiResponse<MissingDataAnalysisResponse>>(
      `/api/data-coverage/participant/${participantId}/missing-data-analysis`,
      payload,
      token,
    );
  }

  static async getStudyMetricCoverage(
    studyId: string,
    payload: StudyMetricCoverageRequest,
    token: string,
  ): Promise<ApiResponse<StudyMetricCoverageResponse>> {
    return authenticatedApi.post<ApiResponse<StudyMetricCoverageResponse>>(
      `/api/data-coverage/study/${studyId}/metric-coverage`,
      payload,
      token,
    );
  }

  static async getStudyMetricDetail(
    studyId: string,
    payload: StudyMetricDetailRequest,
    token: string,
  ): Promise<ApiResponse<StudyMetricDetailResponse>> {
    return authenticatedApi.post<ApiResponse<StudyMetricDetailResponse>>(
      `/api/data-coverage/study/${studyId}/metric-detail`,
      payload,
      token,
    );
  }

  static async getStudyMetricCoverageHeatmap(
    studyId: string,
    payload: StudyMetricCoverageHeatmapRequest,
    token: string,
  ): Promise<ApiResponse<StudyMetricCoverageHeatmapResponse>> {
    return authenticatedApi.post<
      ApiResponse<StudyMetricCoverageHeatmapResponse>
    >(
      `/api/data-coverage/study/${studyId}/metric-coverage-heatmap`,
      payload,
      token,
    );
  }
}
